import { Component, OnInit, inject, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModelService } from '../../services/model.service';
import { ModelFormComponent } from '../model-form/model-form.component';
import { CatalogService } from '@services/data/catalog.service';
import { FormMode } from 'app/core/constants/form-mode';
import { PermissionService } from '@services/core/permission.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-model-list',
    standalone: true,
    imports: [CommonModule, NgSelectModule, FormsModule, ListCardComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './model-list.component.html'
})
export class ModelListComponent implements OnInit, OnChanges {

    @Input() brand: any;

    models: any[] = [];
    filteredModels: any[] = [];

    isLoading = false;
    rows = Array.from({ length: 5 }, (_, i) => i + 1);

    public order: boolean = true;
    public orderColName: string = 'name';

    private modelService = inject(ModelService);
    private modalService = inject(NgbModal);
    private catalogService = inject(CatalogService);
    private router = inject(Router);
    readonly perm = inject(PermissionService);
    public appSettings = inject(AppSettings);

    public Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true
    });

    ngOnInit(): void {
        this.loadData();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['brand']) {
            this.filter();
        }
    }

    loadData() {
        this.isLoading = true;
        this.modelService.get().subscribe({
            next: (res) => {
                this.models = res || [];
                this.filter();
                this.isLoading = false;
            },
            error: (err) => {
                console.error(err);
                this.isLoading = false;
            }
        });
    }

    filter() {
        if (this.brand?.code != null) {
            const brandCode = Number(this.brand.code);
            this.filteredModels = this.models.filter((m) => Number(m.brandCode) === brandCode);
        } else {
            this.filteredModels = [];
        }
        this.applyOrder();
    }

    orderCol(col: string) {
        if (this.orderColName === col) {
            this.order = !this.order;
        } else {
            this.orderColName = col;
            this.order = true;
        }
        this.applyOrder();
    }

    applyOrder() {
        this.filteredModels.sort((a, b) => {
            const valA = a[this.orderColName];
            const valB = b[this.orderColName];

            if (typeof valA === 'string' && typeof valB === 'string') {
                return this.order ? valA.localeCompare(valB) : valB.localeCompare(valA);
            }
            return this.order ? valA - valB : valB - valA;
        });
    }

    openForm(model: any = null) {
        if (!this.brand && !model) return;

        if (this.appSettings.isMobile()) {
            const brandCode = String(model?.brandCode ?? this.brand?.code ?? '');
            const modelCode = model?.code != null ? String(model.code) : 'nuevo';
            if (!brandCode) return;
            void this.router.navigate(['/configuracion/vehiculos', brandCode, 'modelo', modelCode]);
            return;
        }

        const modalRef = this.modalService.open(ModelFormComponent, { size: 'md', backdrop: 'static' });

        modalRef.componentInstance.existingModels = this.models;

        if (model) {
            modalRef.componentInstance.data = model;
            modalRef.componentInstance.mode = FormMode.EDIT;
        } else if (this.brand) {
            modalRef.componentInstance.data = {
                brandCode: this.brand.code,
                year: new Date().getFullYear(),
                isActive: true
            };
            modalRef.componentInstance.mode = FormMode.CREATE;
        }

        modalRef.result.then((result) => {
            if (result) this.upsertModel(result);
        }).catch(() => undefined);
    }

    upsertModel(model: any) {
        if (!model) return;
        const idx = this.models.findIndex((m) =>
            Number(m.code) === Number(model.code)
            || (model.id != null && m.id === model.id)
        );
        if (idx >= 0) {
            this.models[idx] = { ...this.models[idx], ...model };
        } else {
            this.models = [...this.models, model];
        }
        this.filter();
    }

    deleteModel(model: any, event?: Event) {
        event?.stopPropagation();
        if (!this.perm.canCurrent('canDelete') || !model) return;
        const key = model.id ?? model.code;
        if (key == null) return;

        Swal.fire({
            title: `¿Eliminar modelo "${model.name}"?`,
            text: 'Solo se puede eliminar si ningún vehículo de un socio usa este modelo.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        }).then((result) => {
            if (!result.isConfirmed) return;
            this.modelService.delete(key).subscribe({
                next: () => {
                    this.models = this.models.filter((m) => Number(m.code) !== Number(model.code));
                    this.filter();
                    this.catalogService.removeModel(model.code);
                    this.Toast.fire({ icon: 'success', title: 'Modelo eliminado' });
                },
                error: (err) => this.Toast.fire({
                    icon: 'error',
                    title: err?.error?.error || err?.error?.message || 'No se pudo eliminar el modelo'
                })
            });
        });
    }
}
